import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Observable, Subscription, switchMap, of, timer, tap, map, catchError } from 'rxjs';
import { ApiService } from './api.service';
import { TenantService } from './tenant.service';

/**
 * Notification model as returned by the notifications API.
 */
export interface Notification {
  notification_id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
  metadata?: {
    resource_id?: string;
    resource_name?: string;
    reason?: string;
    affected_booking_ids?: string[];
    affected_count?: number;
  };
}

/**
 * Response shape for the paginated notifications list endpoint.
 */
export interface NotificationsListResponse {
  notifications: Notification[];
  last_key: string | null;
}

/**
 * Response shape for the unread count endpoint.
 */
export interface UnreadCountResponse {
  count: number;
}

/**
 * NotificationService manages admin notifications for the tenant portal.
 *
 * Provides methods to:
 * - Fetch paginated notifications
 * - Mark notifications as read/unread
 * - Get the unread notification count
 * - Poll for unread count every 30 seconds (start/stop lifecycle)
 *
 * Uses ApiService for HTTP calls and TenantService for tenant_id resolution.
 *
 * Requirements: 3.4, 3.5
 */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly tenantService = inject(TenantService);

  /** Internal signal for the unread notification count. */
  private readonly _unreadCount = signal<number>(0);

  /** Reactive unread count for UI binding. */
  readonly unreadCount = this._unreadCount.asReadonly();

  /** Whether there are unread notifications. */
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  /** Polling interval in milliseconds (30 seconds). */
  private readonly POLL_INTERVAL_MS = 30_000;

  /** Subscription for the polling timer. */
  private pollingSubscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Fetch paginated notifications for the current tenant.
   *
   * @param lastKey - Optional cursor for pagination (from previous response's last_key)
   * @param limit - Max results per page (default 20, max 100)
   * @returns Observable of the notifications list response
   */
  getNotifications(lastKey?: string, limit: number = 20): Observable<NotificationsListResponse | null> {
    return this.tenantService.tenant$.pipe(
      switchMap((tenant) => {
        if (!tenant) {
          return of(null);
        }
        const params: Record<string, string> = { limit: String(limit) };
        if (lastKey) {
          params['last_key'] = lastKey;
        }
        return this.api
          .get<NotificationsListResponse>(
            `/api/tenants/${tenant.tenant_id}/notifications`,
            params
          )
          .pipe(map((res) => (res.ok && res.data ? res.data : null)));
      })
    );
  }

  /**
   * Mark a notification as read (or unread).
   *
   * @param notificationId - The notification to update
   * @param read - Whether to mark as read (true) or unread (false)
   * @returns Observable of the updated notification
   */
  markAsRead(notificationId: string, read: boolean = true): Observable<Notification | null> {
    return this.tenantService.tenant$.pipe(
      switchMap((tenant) => {
        if (!tenant) {
          return of(null);
        }
        return this.api
          .patch<Notification>(
            `/api/tenants/${tenant.tenant_id}/notifications/${notificationId}`,
            { read }
          )
          .pipe(
            tap((res) => {
              if (res.ok && read) {
                // Decrement unread count optimistically
                this._unreadCount.update((count) => Math.max(0, count - 1));
              } else if (res.ok && !read) {
                // Increment unread count optimistically
                this._unreadCount.update((count) => count + 1);
              }
            }),
            map((res) => (res.ok && res.data ? res.data : null))
          );
      })
    );
  }

  /**
   * Fetch the current unread notification count for the tenant.
   * Updates the internal signal on success.
   *
   * @returns Observable of the unread count
   */
  getUnreadCount(): Observable<number> {
    return this.tenantService.tenant$.pipe(
      switchMap((tenant) => {
        if (!tenant) {
          return of(0);
        }
        return this.api
          .get<UnreadCountResponse>(
            `/api/tenants/${tenant.tenant_id}/notifications/unread-count`
          )
          .pipe(
            tap((res) => {
              if (res.ok && res.data) {
                this._unreadCount.set(res.data.count);
              }
            }),
            map((res) => (res.ok && res.data ? res.data.count : 0)),
            catchError(() => of(0))
          );
      })
    );
  }

  /**
   * Start polling for unread notification count every 30 seconds.
   * Should be called after successful login/tenant load.
   * If polling is already active, this is a no-op.
   */
  startPolling(): void {
    if (this.pollingSubscription) {
      return; // Already polling
    }

    this.pollingSubscription = timer(0, this.POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.getUnreadCount()))
      .subscribe();
  }

  /**
   * Stop the unread count polling.
   * Should be called on logout or when notifications are no longer needed.
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    this._unreadCount.set(0);
  }
}
